<script setup lang="ts">
import {
  CogIcon,
  CircleUserIcon,
  Building2Icon,
  TabletSmartphoneIcon,
  LayoutDashboardIcon,
  ShipIcon,
  LanguagesIcon,
  FileCheckIcon,
  ClipboardCopyIcon,
  ClipboardPenLineIcon,
  ReceiptTextIcon,
  CalculatorIcon,
  WarehouseIcon,
  LinkIcon,
} from 'lucide-vue-next'
import DTieredMenu from './common/DTieredMenu.vue'

const localePath = useLocalePath()
const account = useAccountStore()
const { t } = useI18n()
const router = useRouter()
const { localeMenu } = storeToRefs(useLocaleMenuStore())
const runtimeConfig = useRuntimeConfig()

const ars = useApprovalRequestStore()
await ars.refresh()

const props = withDefaults(
  defineProps<{
    popup?: boolean
    showUserMenu?: boolean
  }>(),
  {
    popup: false,
    showUserMenu: false,
  },
)

const onLogout = async () => {
  await account.logout()
  let loginUrl = '/login'
  if (!runtimeConfig.public.auth0ClientId) {
    loginUrl = '/admin/login'
  }
  router.replace(localePath(loginUrl))
}

const items = computed(() => {
  const menu: DMenuItem[] = [
    {
      label: t('ホーム'),
      pIconClass: 'pi pi-home',
      to: localePath('/'),
      disabled: false,
    },
    {
      label: t('承認'),
      iconComponent: ClipboardPenLineIcon,
      to: localePath('/approval_requests'),
      badge: ars.approvalRequestCount,
      disabled: false,
    },
    {
      label: t('本船スケジュール'),
      iconComponent: ShipIcon,
      //pIconClass: 'pi pi-calendar',
      to: localePath('/vessels'),
      disabled: false,
    },
    {
      label: t('受注'),
      iconComponent: FileCheckIcon,
      disabled: false,
      items: [
        {
          label: t('簡易見積'),
          iconComponent: CalculatorIcon,
          to: localePath('/quickestimate'),
          disabled: false,
        },
        {
          label: t('シミュレーターURL発行'),
          iconComponent: LinkIcon,
          to: localePath('/estimator-token'),
          disabled: false,
        },
        {
          label: t('受注'),
          iconComponent: ClipboardCopyIcon,
          to: localePath('/orders'),
          disabled: false,
        },

        {
          label: t('発行請求書'),
          iconComponent: ReceiptTextIcon,
          to: localePath('/issued_invoices'),
          disabled: false,
        },
        {
          label: t('承認済請求書'),
          iconComponent: ReceiptTextIcon,
          to: localePath('/approved_invoices'),
          disabled: false,
        },
      ],
    },
    {
      label: t('業務'),
      iconComponent: FileCheckIcon,
      disabled: false,
      items: [
        {
          label: t('D2D業務（仮）'),
          pIconClass: 'pi pi-paperclip',
          to: localePath('/tasks'),
          disabled: false,
        },
        {
          label: t('通関業務'),
          pIconClass: 'pi pi-paperclip',
          to: localePath('/customs'),
          disabled: false,
        },
      ],
    },
    {
      label: t('倉庫'),
      iconComponent: WarehouseIcon,
      disabled: false,
      items: [
        {
          label: t('国内保税倉庫'),
          pIconClass: 'pi pi-building',
          to: localePath('/bonded_warehouses'),
          disabled: false,
        },
      ],
    },
    {
      label: t('費用関連'),
      iconComponent: FileCheckIcon,
      disabled: false,
      items: [
        {
          label: t('請求・費用サマリ'),
          iconComponent: CalculatorIcon,
          to: localePath('/billing_and_payments'),
          disabled: false,
        },
        {
          label: t('請求書PDF取り込み'),
          iconComponent: ReceiptTextIcon,
          to: localePath('/procurement_expenses/import'),
          disabled: false,
        },
      ],
    },
    {
      label: t('取引先'),
      iconComponent: Building2Icon,
      disabled: false,
      items: [
        {
          label: t('顧客一覧'),
          pIconClass: 'pi pi-building',
          to: localePath('/clients'),
          disabled: false,
        },
        {
          label: t('代理店一覧'),
          pIconClass: 'pi pi-building',
          to: localePath('/agencies'),
          disabled: false,
        },
        {
          label: t('外注先等一覧'),
          pIconClass: 'pi pi-building',
          to: localePath('/partners'),
          disabled: false,
        },
      ],
    },
    {
      label: t('組織設定'),
      iconComponent: CogIcon,
      disabled: false,
      items: [
        {
          label: t('スタッフ一覧'),
          pIconClass: 'pi pi-users',
          to: localePath('/staff'),
          disabled: false,
        },
        /*        
        {
          label: t('ユーザ一覧'),
          pIconClass: 'pi pi-users',
          to: localePath('/users'),
          disabled: false,
        },
        */
      ],
    },
  ]
  if (props.showUserMenu && account.user) {
    menu.push(
      ...[
        {
          separator: true,
          disabled: false,
        },
        {
          label: account.user?.fullName || account.user?.email,
          iconComponent: CircleUserIcon,
          disabled: false,
          iconClass: 'mr-2 h-4 w-4 shrink-0',
          items: [
            {
              label: t('ユーザー設定'),
              btnDisabled: true,
              pIconClass: 'pi pi-user',
              disabled: false,
            },
            {
              label: t('言語設定'),
              iconComponent: LanguagesIcon,
              items: localeMenu.value as DMenuItem[],
              disabled: false,
            },
            {
              label: t('ログアウト'),
              pIconClass: 'pi pi-sign-out',
              disabled: false,
              myCommand: () => {
                onLogout()
              },
            },
          ],
        },
      ],
    )
  }
  return menu
})
</script>

<template>
  <div v-bind="$attrs">
    <DTieredMenu :items="items" :popup="popup" v-if="popup">
      <template #trigger="{ on }">
        <slot name="trigger" :on="on"></slot>
      </template>
    </DTieredMenu>
    <DMenu
      :items="items"
      :popup="popup"
      class="border-none bg-transparent"
      v-else
    >
      <template #trigger="{ on }">
        <slot name="trigger" :on="on"></slot>
      </template>
    </DMenu>
  </div>
</template>
